import React, { useState } from 'react';
import { X, Award, Zap, Target, Users, Briefcase, TrendingUp, Calendar } from 'lucide-react';
import { SOLDIER_CLASSES, getUnlockedAbilities } from './classes';
import { getColorClasses } from '../pages/themeConfig';

const SoldierProfileModal = ({ soldier, onClose, config, theme = 'vanilla' }) => {
  const [activeTab, setActiveTab] = useState('overview'); // overview, skills, abilities, equipment, history

  const soldierClass = soldier.class ? SOLDIER_CLASSES[soldier.class] : null;
  const unlockedSkills = soldier.unlocked_skills || [];
  const unlockedAbilities = soldierClass ? getUnlockedAbilities(soldier.class, soldier.experience || 0) : [];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Users },
    { id: 'skills', label: 'Skills', icon: Award },
    { id: 'abilities', label: 'Abilities', icon: Zap },
    { id: 'equipment', label: 'Equipment', icon: Briefcase },
    { id: 'history', label: 'History', icon: Calendar }
  ];

  const demoEquipment = {
    weapon: { name: 'Assault Rifle', stats: { damage: 25, accuracy: 75 } },
    armor: { name: 'Standard Vest', stats: { defense: 20, mobility: 80 } },
    utility: { name: 'Medkit', stats: { healing: 50 } }
  };

  const formatSoldierName = () => {
    if (soldier.nickname) {
      return `${soldier.name} "${soldier.nickname}"`;
    }
    return soldier.name;
  };

  const getStats = () => {
    if (!soldierClass) {
      return {
        health: 100,
        aim: 70,
        mobility: 75,
        defense: 50
      };
    }
    return soldierClass.baseStats;
  };

  const stats = getStats();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
      <div className={`bg-gray-800 border-2 ${getColorClasses(config.colors.primary, 'border')} rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col`}>
        
        {/* Header */}
        <div className={`${getColorClasses(config.colors.primary, 'bg')} px-6 py-4 flex items-center justify-between border-b ${getColorClasses(config.colors.primary, 'border')} shrink-0`}>
          <div>
            <h2 className="text-3xl font-bold">{formatSoldierName()}</h2>
            <p className="text-sm text-gray-300 mt-1">{soldier.rank} • {soldierClass ? soldierClass.name : 'Unspecialized'}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={28} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700 bg-gray-900 shrink-0">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 sm:px-6 py-4 font-bold transition-all flex-1 justify-center ${
                  isActive
                    ? `${getColorClasses(config.colors.primary, 'text')} border-b-2 ${getColorClasses(config.colors.primary, 'border')}`
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                <Icon size={18} />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 px-6 py-6">
          
          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Status & Health */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-xs text-gray-400 mb-2">STATUS</p>
                  <p className={`text-2xl font-bold ${
                    soldier.status === 'Ready' ? 'text-green-400' :
                    soldier.status === 'Wounded' ? 'text-red-400' :
                    soldier.status === 'Training' ? 'text-yellow-400' :
                    'text-blue-400'
                  }`}>
                    {soldier.status}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-2">HEALTH</p>
                  <p className="text-2xl font-bold text-green-400">
                    {soldier.health}/{soldier.max_health}
                  </p>
                  <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        soldier.health >= 75 ? 'bg-green-500' :
                        soldier.health >= 50 ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${(soldier.health / soldier.max_health) * 100}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Experience */}
              <div className={`${getColorClasses(config.colors.primary, 'bg')} border ${getColorClasses(config.colors.primary, 'border')} rounded-lg p-4`}>
                <div className="flex items-center justify-between mb-3">
                  <p className="font-bold flex items-center gap-2">
                    <TrendingUp size={18} className={getColorClasses(config.colors.primary)} />
                    Experience
                  </p>
                  <p className="text-sm text-gray-400">{soldier.experience || 0} XP</p>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-3">
                  <div
                    className="bg-purple-500 h-3 rounded-full transition-all"
                    style={{ width: `${Math.min(((soldier.experience || 0) / 1250) * 100, 100)}%` }}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Progress to Command Sergeant Major: {soldier.experience || 0}/1250 XP
                </p>
              </div>

              {/* Combat Stats */}
              <div>
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <Target size={18} className={getColorClasses(config.colors.primary)} />
                  Combat Stats
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-700 rounded-lg p-4">
                    <p className="text-xs text-gray-400 mb-2">HEALTH</p>
                    <p className="text-2xl font-bold text-green-400">{stats.health || 100}</p>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-4">
                    <p className="text-xs text-gray-400 mb-2">AIM</p>
                    <p className="text-2xl font-bold text-blue-400">{stats.aim || 70}%</p>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-4">
                    <p className="text-xs text-gray-400 mb-2">MOBILITY</p>
                    <p className="text-2xl font-bold text-yellow-400">{stats.mobility || 75}%</p>
                  </div>
                  <div className="bg-gray-700 rounded-lg p-4">
                    <p className="text-xs text-gray-400 mb-2">DEFENSE</p>
                    <p className="text-2xl font-bold text-purple-400">{stats.defense || 50}</p>
                  </div>
                </div>
              </div>

              {/* Service Record */}
              <div>
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <Briefcase size={18} className={getColorClasses(config.colors.primary)} />
                  Service Record
                </h3>
                <div className="bg-gray-700 rounded-lg p-4 space-y-2">
                  <p className="text-sm"><span className="text-gray-400">Missions Completed:</span> <span className="font-bold text-green-400">{soldier.missions_completed || 0}</span></p>
                  <p className="text-sm"><span className="text-gray-400">Joined:</span> <span className="font-bold">Day 1</span></p>
                  <p className="text-sm"><span className="text-gray-400">Status:</span> <span className={`font-bold ${soldier.status === 'Ready' ? 'text-green-400' : 'text-yellow-400'}`}>{soldier.status}</span></p>
                </div>
              </div>
            </div>
          )}

          {/* SKILLS TAB */}
          {activeTab === 'skills' && (
            <div className="space-y-4">
              {!soldierClass ? (
                <div className="text-center py-12 text-gray-500">
                  <Award size={48} className="mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-bold">NO SPECIALIZATION</p>
                  <p className="text-sm mt-2">Return to the squad roster and specialize this soldier as a Sergeant to unlock class skills.</p>
                </div>
              ) : (
                <>
                  <div className={`${getColorClasses(config.colors.primary, 'bg')} border ${getColorClasses(config.colors.primary, 'border')} rounded-lg p-4 mb-4`}>
                    <p className="font-bold mb-2 flex items-center gap-2">
                      <span className="text-3xl">{soldierClass.icon}</span>
                      {soldierClass.name}
                    </p>
                    <p className="text-sm text-gray-300">{soldierClass.description}</p>
                  </div>

                  <div>
                    <p className="text-xs text-gray-400 mb-3 font-bold">SKILL TREE ({unlockedSkills.length} / {soldierClass.skills.length} Unlocked)</p>
                    <div className="space-y-3">
                      {soldierClass.skills && soldierClass.skills.length > 0 ? (
                        soldierClass.skills.map((skill) => {
                          const isUnlocked = unlockedSkills.includes(skill.id);
                          const canUnlock = (soldier.experience || 0) >= skill.xpRequired;
                          return (
                            <div
                              key={skill.id}
                              className={`border rounded-lg p-4 transition-all ${
                                isUnlocked
                                  ? `${getColorClasses(config.colors.primary, 'bg')} ${getColorClasses(config.colors.primary, 'border')}`
                                  : canUnlock ? 'border-yellow-600 bg-gray-700' : 'border-gray-600 bg-gray-700 opacity-50'
                              }`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <p className="font-bold">{skill.name}</p>
                                    {isUnlocked && (
                                      <span className="text-xs bg-green-500 text-white px-2 py-1 rounded">UNLOCKED</span>
                                    )}
                                    {!isUnlocked && canUnlock && (
                                      <span className="text-xs bg-yellow-600 text-white px-2 py-1 rounded">READY</span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-400 mt-1">{skill.description}</p>
                                  <p className="text-xs text-gray-500 mt-2">Tier {skill.tier}</p>
                                </div>
                                <p className="text-xs text-gray-500 ml-4 whitespace-nowrap">
                                  {skill.xpRequired} XP
                                </p>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-sm text-gray-500">No skills available for this class yet.</p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ABILITIES TAB */}
          {activeTab === 'abilities' && (
            <div className="space-y-4">
              {!soldierClass ? (
                <div className="text-center py-12 text-gray-500">
                  <Zap size={48} className="mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-bold">NO SPECIALIZATION</p>
                  <p className="text-sm mt-2">Specialize this soldier to unlock combat abilities.</p>
                </div>
              ) : (
                <>
                  <div className={`${getColorClasses(config.colors.primary, 'bg')} border ${getColorClasses(config.colors.primary, 'border')} rounded-lg p-4 mb-4`}>
                    <p className="font-bold mb-2">Combat Abilities</p>
                    <p className="text-sm text-gray-300">Unlocked {unlockedAbilities.length} / {soldierClass.abilities?.length || 0} abilities</p>
                  </div>

                  <div className="space-y-3">
                    {soldierClass.abilities && soldierClass.abilities.length > 0 ? (
                      soldierClass.abilities.map((ability) => {
                        const isUnlocked = ability.unlockAtXP <= (soldier.experience || 0);
                        return (
                          <div
                            key={ability.id}
                            className={`border rounded-lg p-4 transition-all ${
                              isUnlocked
                                ? `${getColorClasses(config.colors.primary, 'bg')} ${getColorClasses(config.colors.primary, 'border')}`
                                : 'border-gray-600 bg-gray-700 opacity-50'
                            }`}
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-bold">{ability.name}</p>
                                  {isUnlocked && (
                                    <span className="text-xs bg-green-500 text-white px-2 py-1 rounded">UNLOCKED</span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-400 mt-1">{ability.description}</p>
                                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                  <span>Cooldown: {ability.cooldown}</span>
                                  {ability.usesPerMission && (
                                    <span>Uses/Mission: {ability.usesPerMission}</span>
                                  )}
                                </div>
                              </div>
                              <p className="text-xs text-gray-500 ml-4 whitespace-nowrap">
                                Unlock: {ability.unlockAtXP} XP
                              </p>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-sm text-gray-500">No abilities available.</p>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* EQUIPMENT TAB */}
          {activeTab === 'equipment' && (
            <div className="space-y-6">
              <div className={`${getColorClasses(config.colors.primary, 'bg')} border ${getColorClasses(config.colors.primary, 'border')} rounded-lg p-4 mb-4`}>
                <p className="text-sm text-gray-300">
                  Equipment is configured during mission deployment. Visit the mission briefing screen to customize loadouts.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-6">
                {/* Weapon */}
                <div className="bg-gray-700 rounded-lg p-6 border border-gray-600">
                  <p className="text-xs text-gray-400 mb-3 font-bold">PRIMARY WEAPON</p>
                  <p className="text-2xl mb-2">🔫</p>
                  <p className="font-bold mb-1">{demoEquipment.weapon.name}</p>
                  <div className="space-y-1 text-sm">
                    <p className="text-gray-400">DMG: <span className="text-white font-bold">{demoEquipment.weapon.stats.damage}</span></p>
                    <p className="text-gray-400">ACC: <span className="text-white font-bold">{demoEquipment.weapon.stats.accuracy}%</span></p>
                  </div>
                </div>

                {/* Armor */}
                <div className="bg-gray-700 rounded-lg p-6 border border-gray-600">
                  <p className="text-xs text-gray-400 mb-3 font-bold">BODY ARMOR</p>
                  <p className="text-2xl mb-2">🛡️</p>
                  <p className="font-bold mb-1">{demoEquipment.armor.name}</p>
                  <div className="space-y-1 text-sm">
                    <p className="text-gray-400">DEF: <span className="text-white font-bold">{demoEquipment.armor.stats.defense}</span></p>
                    <p className="text-gray-400">MOB: <span className="text-white font-bold">{demoEquipment.armor.stats.mobility}%</span></p>
                  </div>
                </div>

                {/* Utility */}
                <div className="bg-gray-700 rounded-lg p-6 border border-gray-600">
                  <p className="text-xs text-gray-400 mb-3 font-bold">UTILITY</p>
                  <p className="text-2xl mb-2">💊</p>
                  <p className="font-bold mb-1">{demoEquipment.utility.name}</p>
                  <div className="space-y-1 text-sm">
                    <p className="text-gray-400">Effect: <span className="text-white font-bold">Healing</span></p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* HISTORY TAB */}
          {activeTab === 'history' && (
            <div className="space-y-4">
              <div className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                <p className="font-bold mb-2">Mission Log</p>
                <div className="space-y-3">
                  {soldier.missions_completed === 0 ? (
                    <p className="text-sm text-gray-400">No missions completed yet. Deploy this soldier to begin their record.</p>
                  ) : (
                    [...Array(Math.min(soldier.missions_completed, 5))].map((_, i) => (
                      <div key={i} className="text-sm border-l-2 border-purple-500 pl-3">
                        <p className="font-bold">Mission {i + 1}</p>
                        <p className="text-xs text-gray-400">Status: Completed • +50 XP</p>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                <p className="font-bold mb-2">Promotion History</p>
                <div className="text-sm border-l-2 border-yellow-500 pl-3">
                  <p className="font-bold">Promoted to {soldier.rank}</p>
                  <p className="text-xs text-gray-400">Day 1 • Upon recruitment</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-700 bg-gray-900 px-6 py-4 flex justify-end shrink-0">
          <button
            onClick={onClose}
            className={`${getColorClasses(config.colors.primary, 'bgMain')} ${getColorClasses(config.colors.primary, 'bgHover')} px-6 py-2 rounded font-bold transition-all`}
          >
            Close Profile
          </button>
        </div>
      </div>
    </div>
  );
};

export default SoldierProfileModal;